import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  calculateExit, closeExit, loadAssetAssignments, loadAssets, loadDirectory, loadExitTasks, loadExits, loadLeavingReasons, setExitTask,
  settleExit, startExit, updateExit, type ExitCase, type ExitTask, type LeavingReason,
} from "../data";
import { money } from "../lib/payslip";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TYPES = ["resignation", "termination", "contract_end", "retirement", "mutual", "other"];
const tone: Record<ExitCase["status"], string> = { open: "warning", settled: "info", closed: "success", cancelled: "neutral" };
type Info = Awaited<ReturnType<typeof calculateExit>>;

/** Offboarding: exits in progress with their checklist, exit interview and final settlement into payroll. */
export function Offboarding({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const manage = ws.permissions.has("employees.manage");
  const [cases, setCases] = useState<ExitCase[] | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [reasons, setReasons] = useState<LeavingReason[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [f, setF] = useState({ emp: "", type: "resignation", notice: iso(new Date()), last: iso(new Date(Date.now() + 90 * 864e5)), reason: "", notes: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadExits(cid).then(setCases).catch((e) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void refresh(); void loadDirectory(cid).then(setPeople); void loadLeavingReasons().then(setReasons); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const daysLeft = (d: string) => Math.round((new Date(`${d}T00:00:00`).getTime() - new Date(iso(new Date()) + "T00:00:00").getTime()) / 864e5);

  async function start(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { const id = await startExit(f.emp, f.type, f.last, f.reason || null, f.notice, f.notes); await refresh(); setOpen(id); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  const c = cases?.find((x) => x.id === open);
  if (c) return <div className="page"><CaseView ws={ws} c={c} name={name(c.employment_id)} reasons={reasons} onBack={() => { setOpen(null); void refresh(); }} onOpen={onOpen} onChanged={refresh} /></div>;

  const active = (cases ?? []).filter((x) => x.status === "open" || x.status === "settled");
  const done = (cases ?? []).filter((x) => x.status === "closed" || x.status === "cancelled");
  const row = (x: ExitCase) => (
    <tr key={x.id}>
      <td><button type="button" className="link strong" onClick={() => setOpen(x.id)}>{name(x.employment_id)}</button></td>
      <td>{t(`xType_${x.exit_type}` as StringKey)}</td>
      <td className="mono">{x.last_day}{x.status === "open" && daysLeft(x.last_day) >= 0 ? <span className="muted small-note"> · {t("inDays").replace("{n}", String(daysLeft(x.last_day)))}</span> : null}</td>
      <td><span className={`badge ${tone[x.status]}`}>{t(`xStatus_${x.status}` as StringKey)}</span></td>
    </tr>
  );
  return (
    <div className="page">
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <section className="card">
        <h2>{t("xInProgress")}</h2>
        {cases === null ? <p className="muted">{t("loading")}</p> : active.length === 0 ? <p className="muted">{t("xNone")}</p> : (
          <div className="table-wrap"><table><thead><tr><th>{t("employee")}</th><th>{t("otKind")}</th><th>{t("xLastDay")}</th><th>{t("status")}</th></tr></thead><tbody>{active.map(row)}</tbody></table></div>
        )}
        <p className="muted small-note">{t("xRules")}</p>
      </section>
      {manage && (
        <form className="card" onSubmit={start}>
          <h2>{t("xStart")}</h2>
          <div className="field-row">
            <label className="field"><span>{t("employee")}</span>
              <select value={f.emp} onChange={(e) => setF({ ...f, emp: e.target.value })} required>
                <option value="">—</option>
                {people.filter((p) => p.status !== "terminated" && p.status !== "starting").map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}</option>)}
              </select></label>
            <label className="field"><span>{t("otKind")}</span>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{TYPES.map((x) => <option key={x} value={x}>{t(`xType_${x}` as StringKey)}</option>)}</select></label>
            <label className="field"><span>{t("xNoticeDate")}</span><input type="date" value={f.notice} onChange={(e) => setF({ ...f, notice: e.target.value })} required /></label>
            <label className="field"><span>{t("xLastDay")}</span><input type="date" value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} required /></label>
          </div>
          <div className="field-row">
            <label className="field"><span>{t("leavingReason")}</span>
              <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
                <option value="">—</option>{reasons.map((r) => <option key={r.code} value={r.code}>{pick(r.name_en, r.name_ar)}</option>)}
              </select></label>
            <label className="field"><span>{t("note")}</span><input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
          </div>
          <div className="actions"><button className="btn primary">{t("xStartBtn")}</button></div>
        </form>
      )}
      {done.length > 0 && (
        <details className="card"><summary>{t("xDone")} ({done.length})</summary>
          <div className="table-wrap"><table><tbody>{done.map(row)}</tbody></table></div>
        </details>
      )}
    </div>
  );
}

function CaseView({ ws, c, name, reasons, onBack, onOpen, onChanged }: {
  ws: Workspace; c: ExitCase; name: string; reasons: LeavingReason[]; onBack: () => void; onOpen: (id: string) => void; onChanged: () => Promise<void>;
}) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const editable = c.status === "open" && ws.permissions.has("employees.manage");
  const [tasks, setTasks] = useState<ExitTask[]>([]);
  const [info, setInfo] = useState<Info | null>(null);
  const [held, setHeld] = useState<{ tag: string; name: string; value: number | null }[]>([]);
  const [d, setD] = useState(c);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { setD(c); }, [c]);
  const refreshTasks = () => loadExitTasks(c.id).then(setTasks);
  useEffect(() => {
    void refreshTasks();
    if (ws.enabled.has("assets")) void Promise.all([loadAssets(cid), loadAssetAssignments(cid)]).then(([as, aa]) =>
      setHeld(aa.filter((x) => !x.returned_on && x.employment_id === c.employment_id).map((x) => as.find((a) => a.id === x.asset_id)).filter(Boolean).map((a) => ({ tag: a!.tag, name: a!.name, value: a!.value }))));
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function run(fn: () => Promise<unknown>, done?: string) {
    setMsg(null);
    try { await fn(); await onChanged(); if (done) setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const saveDetails = () => run(() => updateExit(c.id, {
    exit_type: d.exit_type, last_day: d.last_day, leaving_reason_code: d.leaving_reason_code, regrettable: d.regrettable, rehire_eligible: d.rehire_eligible,
    notice_months: Number(d.notice_months), leave_amount: Number(d.leave_amount), notice_amount: Number(d.notice_amount), gratuity_amount: Number(d.gratuity_amount),
    compensation_amount: Number(d.compensation_amount), loan_amount: Number(d.loan_amount), asset_deduction: Number(d.asset_deduction), notes: d.notes,
  }), t("saved"));
  const calc = () => run(async () => { await saveDetails(); setInfo(await calculateExit(c.id)); }, t("xCalculated"));
  const net = Number(d.leave_amount) + Number(d.notice_amount) + Number(d.gratuity_amount) + Number(d.compensation_amount) - Number(d.loan_amount) - Number(d.asset_deduction);
  const num = (k: "leave_amount" | "notice_amount" | "gratuity_amount" | "compensation_amount" | "loan_amount" | "asset_deduction", label: StringKey, minus = false) => (
    <label className="field"><span>{minus ? "− " : "+ "}{t(label)}</span>
      <input type="number" step="0.01" min="0" disabled={!editable} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value as unknown as number })} /></label>
  );
  const iv = d.interview as { reason?: string; would_recommend?: boolean; rating_manager?: number; rating_pay?: number; rating_growth?: number; comments?: string };
  const setIv = (patch: Partial<typeof iv>) => setD({ ...d, interview: { ...iv, ...patch } });

  return (
    <>
      <button type="button" className="link back" onClick={onBack}>← {t("navOffboarding")}</button>
      <section className="card">
        <div className="row spread">
          <h2><button type="button" className="link strong" onClick={() => onOpen(c.employment_id)}>{name}</button> <span className={`badge ${tone[c.status]}`}>{t(`xStatus_${c.status}` as StringKey)}</span></h2>
          <span className="muted small-note">{t("xNoticeDate")} <span className="mono">{c.notice_date}</span></span>
        </div>
        {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
        <div className="field-row">
          <label className="field"><span>{t("otKind")}</span>
            <select disabled={!editable} value={d.exit_type} onChange={(e) => setD({ ...d, exit_type: e.target.value })}>{TYPES.map((x) => <option key={x} value={x}>{t(`xType_${x}` as StringKey)}</option>)}</select></label>
          <label className="field"><span>{t("xLastDay")}</span><input type="date" disabled={!editable} value={d.last_day} onChange={(e) => setD({ ...d, last_day: e.target.value })} /></label>
          <label className="field"><span>{t("leavingReason")}</span>
            <select disabled={!editable} value={d.leaving_reason_code ?? ""} onChange={(e) => setD({ ...d, leaving_reason_code: e.target.value || null })}>
              <option value="">—</option>{reasons.map((r) => <option key={r.code} value={r.code}>{pick(r.name_en, r.name_ar)}</option>)}
            </select></label>
        </div>
        <div className="row">
          <label className="check"><input type="checkbox" disabled={!editable} checked={!!d.regrettable} onChange={(e) => setD({ ...d, regrettable: e.target.checked })} /> {t("regrettable")}</label>
          <label className="check"><input type="checkbox" disabled={!editable} checked={d.rehire_eligible !== false} onChange={(e) => setD({ ...d, rehire_eligible: e.target.checked })} /> {t("rehireEligible")}</label>
        </div>
      </section>

      <section className="card">
        <h2>{t("xChecklist")}</h2>
        <ul className="req-list">
          {tasks.map((x) => (
            <li key={x.id}>
              <label className="check"><input type="checkbox" checked={!!x.done_at} disabled={!ws.permissions.has("employees.manage") || c.status === "closed" || c.status === "cancelled"}
                onChange={(e) => void run(async () => { await setExitTask(x.id, e.target.checked); await refreshTasks(); })} /> {pick(x.title_en, x.title_ar)}</label>
              <span className="muted small-note">{t(`xOwner_${x.owner}` as StringKey)}</span>
            </li>
          ))}
        </ul>
        {held.length > 0 && <p className="alert warning">{t("xStillHolds")}: {held.map((h) => `${h.name} (${h.tag}${h.value ? `, ${money(h.value)}` : ""})`).join(", ")}</p>}
      </section>

      <section className="card">
        <h2>{t("xSettlement")}</h2>
        {info && (
          <p className="muted small-note">{t("xInfo").replace("{pay}", money(info.monthly_pay)).replace("{daily}", money(info.daily_wage)).replace("{years}", String(info.years_of_service))}
            {!info.insured && ` ${t("xUninsured")}`} {d.exit_type === "termination" && t("xCourtMin").replace("{amt}", money(info.dismissal_court_minimum))}</p>
        )}
        <div className="field-row">
          <label className="field"><span>{t("xLeaveDays")}</span><input type="number" disabled value={d.leave_days} /></label>
          {num("leave_amount", "xLeaveAmount")}
          <label className="field"><span>{t("xNoticeMonths")}</span><input type="number" step="0.5" min="0" max="3" disabled={!editable} value={d.notice_months}
            onChange={(e) => setD({ ...d, notice_months: e.target.value as unknown as number })} /></label>
          {num("notice_amount", "xNoticePay")}
        </div>
        <div className="field-row">
          {num("gratuity_amount", "xGratuity")}
          {num("compensation_amount", "xCompensation")}
          {num("loan_amount", "xLoan", true)}
          {num("asset_deduction", "xAssets", true)}
        </div>
        <div className="row spread"><span className="strong">{t("xNet")}</span><span className="mono strong">{money(net)}</span></div>
        <p className="muted small-note">{t("xSettlementHelp")}</p>
        {editable && (
          <div className="row">
            <button type="button" className="btn secondary" onClick={calc}>{t("xCalculate")}</button>
            <button type="button" className="btn secondary" onClick={saveDetails}>{t("save")}</button>
            {ws.permissions.has("payroll.manage") && (
              <button type="button" className="btn primary" onClick={() => { if (window.confirm(t("xSettleAsk"))) void run(async () => { await saveDetails(); await settleExit(c.id); }, t("xSettled")); }}>{t("xSettle")}</button>
            )}
          </div>
        )}
        {c.settlement_run_id && <p className="alert info">{t("xInPayroll").replace("{m}", c.last_day.slice(0, 7))}</p>}
      </section>

      <section className="card">
        <h2>{t("xInterview")}</h2>
        <div className="field-row">
          <label className="field"><span>{t("xMainReason")}</span>
            <select disabled={!editable} value={iv.reason ?? ""} onChange={(e) => setIv({ reason: e.target.value })}>
              <option value="">—</option>{["career", "pay", "manager", "workload", "relocation", "study", "family", "other"].map((r) => <option key={r} value={r}>{t(`xR_${r}` as StringKey)}</option>)}
            </select></label>
          {(["rating_manager", "rating_pay", "rating_growth"] as const).map((k) => (
            <label key={k} className="field"><span>{t(`x_${k}` as StringKey)} (1–5)</span>
              <input type="number" min="1" max="5" disabled={!editable} value={iv[k] ?? ""} onChange={(e) => setIv({ [k]: Number(e.target.value) || undefined })} /></label>
          ))}
        </div>
        <label className="check"><input type="checkbox" disabled={!editable} checked={!!iv.would_recommend} onChange={(e) => setIv({ would_recommend: e.target.checked })} /> {t("xRecommend")}</label>
        <label className="field"><span>{t("xComments")}</span><textarea rows={3} disabled={!editable} value={iv.comments ?? ""} onChange={(e) => setIv({ comments: e.target.value })} /></label>
        {editable && <div className="actions"><button type="button" className="btn secondary" onClick={() => run(() => updateExit(c.id, { interview: d.interview }), t("saved"))}>{t("save")}</button></div>}
      </section>

      {ws.permissions.has("employees.manage") && (c.status === "open" || c.status === "settled") && (
        <div className="row">
          {c.status === "settled" && <button type="button" className="btn primary" onClick={() => run(() => closeExit(c.id, false), t("xClosed"))}>{t("xClose")}</button>}
          {c.status === "open" && <button type="button" className="btn secondary" onClick={() => { if (window.confirm(t("xWithdrawAsk"))) void run(() => closeExit(c.id, true), t("xWithdrawn")); }}>{t("xWithdraw")}</button>}
        </div>
      )}
    </>
  );
}

/** Self-service: resign (opens the exit with HR). */
export function MyResignation({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState<ExitCase | null>(null);
  const [f, setF] = useState({ last: iso(new Date(Date.now() + 90 * 864e5)), notes: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadExits(ws.membership.company_id).then((r) => setMine(r.find((x) => x.employment_id === ws.membership.employment_id && (x.status === "open" || x.status === "settled")) ?? null));
  useEffect(() => { void refresh(); }, [ws.membership.company_id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function send(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { await startExit(ws.membership.employment_id!, "resignation", f.last, null, iso(new Date()), f.notes); setOpen(false); setMsg({ ok: true, text: t("xResignSent") }); await refresh(); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  return (
    <section className="card">
      <div className="row spread">
        <h2>{t("xResignTitle")}</h2>
        {!open && !mine && <button type="button" className="btn secondary small" onClick={() => setOpen(true)}>{t("xResign")}</button>}
      </div>
      {mine && <p className="alert info">{t("xResignOpen").replace("{d}", mine.last_day)}</p>}
      {open && (
        <form className="stack" onSubmit={send}>
          <p className="muted small-note">{t("xResignHelp")}</p>
          <div className="field-row">
            <label className="field"><span>{t("xLastDay")}</span><input type="date" value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} required /></label>
            <label className="field"><span>{t("note")}</span><input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
          </div>
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setOpen(false)}>{t("cancel")}</button>
            <button className="btn danger">{t("xResignSend")}</button>
          </div>
        </form>
      )}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
    </section>
  );
}
